import React, { useState, useEffect } from 'react';
import { Trash2, ChevronsDown, ChevronsUp, ChevronRight, Table2, TreePine } from 'lucide-react';
import { buildRenderTree, toggleNodeExpanded, expandAll, collapseAll } from '../utils/treeBuilder';
import JsonTreeView from './JsonTreeView';
import PreviewTable from './PreviewTable';
import ResizableSplitter from './ResizableSplitter';
import ErrorPanel from './ErrorPanel';
import HistoryPanel, { historyManager } from './HistoryPanel';
import { useLanguage } from '../i18n';

/**
 * JSON 工具视图组件
 * 功能：树状视图/表格视图切换、可拖动分隔
 * @param {string} tabId - 标签页 ID，用于独立存储每个标签的状态
 * @param {Object} theme - 主题配置
 * @param {function} onJsonParsed - JSON解析成功的回调，用于更新标签名
 */
function JsonFormatView({ tabId = '', theme, onJsonParsed }) {
  const { t, language } = useLanguage();

  // LocalStorage keys - 根据 tabId 生成独立的 key
  const STORAGE_KEY_WIDTH = tabId ? `json-format-view-width-${tabId}` : 'json-format-view-width';
  const STORAGE_KEY_VIEW = tabId ? `json-format-view-mode-${tabId}` : 'json-format-view-mode';
  const STORAGE_KEY_INPUT = tabId ? `json-input-${tabId}` : 'json-input';

  const [input, setInput] = useState(() => {
    // 从 localStorage 恢复输入内容
    const saved = localStorage.getItem(STORAGE_KEY_INPUT);
    return saved || '';
  });
  const [error, setError] = useState('');
  const [treeData, setTreeData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null); // 选中的节点
  const [historyVersion, setHistoryVersion] = useState(0); // 用于通知历史记录更新

  // 视图模式：'tree' | 'table'
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_VIEW);
    return saved || 'tree';
  });

  // 读取保存的宽度或使用默认值 50%
  const [leftWidth, setLeftWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_WIDTH);
    return saved ? parseFloat(saved) : 50;
  });

  // 保存输入内容到 localStorage
  useEffect(() => {
    if (input) {
      localStorage.setItem(STORAGE_KEY_INPUT, input);
    } else {
      localStorage.removeItem(STORAGE_KEY_INPUT);
    }
  }, [input, STORAGE_KEY_INPUT]);

  const [isDragging, setIsDragging] = useState(false);
  const isInputCollapsed = leftWidth === 0;

  // 详情模式开关
  const [detailMode, setDetailMode] = useState(false);

  // 切换视图模式
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem(STORAGE_KEY_VIEW, mode);
  };

  // 清空输入
  const handleClear = () => {
    setInput('');
    setError('');
    setTreeData(null);
    setSelectedNode(null); // 清空选中节点
    // 恢复输入框展开状态
    setLeftWidth(50);
    localStorage.setItem(STORAGE_KEY_WIDTH, '50');
  };

  // 处理节点选中（仅在详情模式下生效）
  const handleNodeSelect = (node) => {
    // 如果详情模式未开启，不处理
    if (!detailMode) return;

    // 如果输入框未折叠，先折叠输入框
    if (!isInputCollapsed) {
      setLeftWidth(0);
      localStorage.setItem(STORAGE_KEY_WIDTH, '0');
    }

    // 如果点击的是同一个节点，取消选中
    if (selectedNode && selectedNode.id === node.id) {
      setSelectedNode(null);
    } else {
      setSelectedNode(node);
    }
  };

  // 处理宽度变化
  const handleResize = (newWidth) => {
    setLeftWidth(newWidth);
    // 保存到 localStorage
    localStorage.setItem(STORAGE_KEY_WIDTH, newWidth.toString());
  };

  // 展开输入框
  const handleExpandInput = () => {
    const newWidth = 50; // 恢复到默认宽度
    setLeftWidth(newWidth);
    localStorage.setItem(STORAGE_KEY_WIDTH, newWidth.toString());
  };

  // 处理拖动状态变化
  const handleDragStateChange = (dragging) => {
    setIsDragging(dragging);
  };

  // 确保初始状态时输入框展开
  useEffect(() => {
    if (!input && !treeData && leftWidth === 0) {
      // 如果没有输入内容且没有树状数据，且输入框是折叠的，则展开输入框
      setLeftWidth(50);
      localStorage.setItem(STORAGE_KEY_WIDTH, '50');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次

  // 自动解析
  useEffect(() => {
    if (!input.trim()) {
      setTreeData(null);
      setError('');
      return;
    }

    // 防抖：停止输入 300ms 后解析
    const timer = setTimeout(() => {
      try {
        let parsed = JSON.parse(input);

        // 智能识别转义字符串
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }

        const tree = buildRenderTree(parsed);
        setTreeData(tree);
        setError('');

        // 解析成功后，保存到历史记录
        if (tree) {
          historyManager.add(input);
          setHistoryVersion(prev => prev + 1); // 通知历史记录已更新

          // 通知父组件JSON解析成功，用于自动命名
          if (onJsonParsed) {
            onJsonParsed(input);
          }
          // 不再自动折叠输入框，让用户点击节点时再折叠
        }
      } catch (err) {
        setTreeData(null);
        setError(`解析失败: ${err.message}`);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [input]);

  // 切换节点展开状态
  const handleToggleNode = (nodeId) => {
    if (treeData) {
      const newTree = toggleNodeExpanded(treeData, nodeId);
      setTreeData(newTree);
    }
  };

  // 全部展开
  const handleExpandAll = () => {
    if (treeData) {
      const newTree = expandAll(treeData);
      setTreeData(newTree);
    }
  };

  // 全部折叠
  const handleCollapseAll = () => {
    if (treeData) {
      const newTree = collapseAll(treeData);
      setTreeData(newTree);
    }
  };

  // 处理修复后的 JSON
  const handleFixedJson = (fixedJson) => {
    setInput(fixedJson);
    setError(''); // 清除错误
  };

  // 从历史记录加载
  const handleLoadHistory = (content) => {
    setInput(content);
    // 展开输入框以便查看
    setLeftWidth(50);
    localStorage.setItem(STORAGE_KEY_WIDTH, '50');
  };

  return (
    <div className="flex flex-col h-[calc(100vh-180px)] min-h-[400px]">
      {/* 主内容卡片 */}
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden flex flex-col">
        {/* 工具栏 */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-2 items-center">
          {/* 历史记录 */}
          <HistoryPanel onLoad={handleLoadHistory} version={historyVersion} />

          {/* 分隔线 */}
          <div className="border-l border-gray-200 dark:border-gray-700 h-8"></div>

          {/* 视图模式切换 */}
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => handleViewModeChange('table')}
              className={`px-3 py-1.5 text-sm font-medium flex items-center gap-2 rounded-md transition-all duration-200 ${
                viewMode === 'table'
                  ? `${theme?.light || 'bg-blue-50 dark:bg-blue-900/20'} ${theme?.text || 'text-blue-600 dark:text-blue-400'} shadow-sm`
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Table2 size={16} />
              {t('json.toolbar.table')}
            </button>
            <button
              onClick={() => handleViewModeChange('tree')}
              className={`px-3 py-1.5 text-sm font-medium flex items-center gap-2 rounded-md transition-all duration-200 ${
                viewMode === 'tree'
                  ? `${theme?.light || 'bg-blue-50 dark:bg-blue-900/20'} ${theme?.text || 'text-blue-600 dark:text-blue-400'} shadow-sm`
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <TreePine size={16} />
              {t('json.toolbar.tree')}
            </button>
          </div>

          {/* 分隔线 */}
          <div className="border-l border-gray-200 dark:border-gray-700 h-8"></div>

          {/* 全部展开按钮 */}
          <button
            onClick={handleExpandAll}
            disabled={!treeData}
            className="px-3 py-1.5 text-sm font-medium border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 flex items-center gap-2 text-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronsDown size={16} />
            {t('json.toolbar.expandAll')}
          </button>

          {/* 全部折叠按钮 */}
          <button
            onClick={handleCollapseAll}
            disabled={!treeData}
            className="px-3 py-1.5 text-sm font-medium border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 flex items-center gap-2 text-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronsUp size={16} />
            {t('json.toolbar.collapseAll')}
          </button>

          {/* 分隔线 */}
          <div className="border-l border-gray-200 dark:border-gray-700 h-8"></div>

          {/* JSON节点详情开关 */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <span className={`text-sm font-medium ${
                !treeData || viewMode !== 'tree'
                  ? 'text-gray-400 dark:text-gray-500'
                  : 'text-gray-700 dark:text-gray-300'
              }`}>
                {language === 'zh' ? 'JSON节点详情' : 'JSON Node Detail'}
              </span>
              {/* Toggle Switch */}
              <button
                onClick={() => {
                  if (!treeData || viewMode !== 'tree') return;
                  setDetailMode(!detailMode);
                  if (!detailMode) {
                    setSelectedNode(null);
                  }
                }}
                disabled={!treeData || viewMode !== 'tree'}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                  detailMode
                    ? 'bg-purple-500'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    detailMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
            {/* 详情模式提示 */}
            {detailMode && treeData && viewMode === 'tree' && (
              <span className="text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                {language === 'zh' ? '点击节点查看详情' : 'Click node to view detail'}
              </span>
            )}
          </div>

          {/* 分隔线 */}
          <div className="border-l border-gray-200 dark:border-gray-700 h-8"></div>

          {/* 清空按钮 */}
          <button
            onClick={handleClear}
            className="px-3 py-1.5 text-sm font-medium border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800 transition-all duration-200 flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400"
          >
            <Trash2 size={16} />
            {t('json.toolbar.clear')}
          </button>
        </div>

        {/* 错误提示面板 */}
        {error && (
          <ErrorPanel
            error={error}
            originalInput={input}
            onFixed={handleFixedJson}
          />
        )}
        </div>

        {/* 内容区：左右分栏 */}
        <div className="flex flex-1 overflow-hidden relative">
        {/* 展开按钮（折叠时显示） - 悬浮展开标签 */}
        {isInputCollapsed && (
          <button
            onClick={handleExpandInput}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-600 dark:text-gray-200 hover:text-gray-700 dark:hover:text-gray-100 rounded-r-md shadow-md hover:shadow-lg transition-all duration-300 flex flex-col items-center justify-center gap-1 group overflow-hidden"
            style={{
              padding: '20px 4px',
              width: '20px'
            }}
            title={t('json.input.expandInput')}
            onMouseEnter={(e) => {
              e.currentTarget.style.width = '40px';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.width = '20px';
            }}
          >
            <ChevronRight size={16} className="flex-shrink-0" />
            <div
              className="flex flex-col items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ writingMode: 'vertical-rl' }}
            >
              <span className="text-xs font-medium tracking-wider whitespace-nowrap">{t('json.input.expandInput')}</span>
            </div>
          </button>
        )}

        {/* 左侧：输入区域 */}
        {!isInputCollapsed && (
          <div
            className={`flex flex-col border-r border-gray-200 dark:border-gray-700 ${isDragging ? '' : 'transition-all duration-500 ease-out'}`}
            style={{ width: `${leftWidth}%` }}
          >
            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{t('json.input.title')}</h3>
            </div>
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder=""
                className="absolute inset-0 p-4 font-mono text-sm resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
                style={{ overscrollBehavior: 'contain' }}
              />
              {/* 空状态功能提示 */}
              {!input && (
                <div className="absolute inset-0 p-4 pointer-events-none">
                  <div className="text-gray-400 dark:text-gray-500 font-mono text-sm mb-4">{t('json.input.placeholder')}</div>
                  <div className="mt-6 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/30">
                    <div className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-3 flex items-center gap-2">
                      <span>{t('json.features.highlights')}</span>
                    </div>
                    <ul className="space-y-2 text-xs text-blue-600/80 dark:text-blue-400/80">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>{t('json.features.innerJson.title')}</strong> - {t('json.features.innerJson.short')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>{t('json.features.tableView.title')}</strong> - {t('json.features.tableView.short')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>{t('json.features.nodeDetail.title')}</strong> - {t('json.features.nodeDetail.short')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>{t('json.features.multiTab.title')}</strong> - {t('json.features.multiTab.short')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>{t('json.features.historyRecord.title')}</strong> - {t('json.features.historyRecord.short')}</span>
                      </li>
                    </ul>

                    {/* 示例表格 - 展示 API 响应风格 */}
                    <div className="mt-4 border border-blue-100 dark:border-blue-900/30 rounded-lg overflow-hidden">
                      <div className="text-xs font-medium text-blue-700 dark:text-blue-400 px-3 py-2 bg-blue-100/50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/30">
                        {language === 'zh' ? '表格视图示例 (API Response)' : 'Table View Example (API Response)'}
                      </div>
                      {/* 示例表格头 */}
                      <div className="flex text-xs font-semibold bg-indigo-50/50 dark:bg-indigo-900/20 border-b border-blue-100 dark:border-blue-900/30">
                        <div className="w-1/2 px-3 py-1.5 text-indigo-700 dark:text-indigo-300 border-r border-blue-100 dark:border-blue-900/30">Key</div>
                        <div className="w-1/2 px-3 py-1.5 text-indigo-700 dark:text-indigo-300">Value</div>
                      </div>
                      {/* 示例数据 - API响应风格 */}
                      <div className="text-xs bg-white dark:bg-gray-800/50">
                        {/* root */}
                        <div className="flex border-b border-blue-50 dark:border-blue-900/20">
                          <div className="w-1/2 px-3 py-1 text-gray-600 dark:text-gray-400 border-r border-blue-50 dark:border-blue-900/20">
                            <span className="text-gray-400 mr-1">▼</span> root
                          </div>
                          <div className="w-1/2 px-3 py-1 text-purple-600 dark:text-purple-400">{'{...}'}</div>
                        </div>
                        {/* code */}
                        <div className="flex border-b border-blue-50 dark:border-blue-900/20">
                          <div className="w-1/2 px-3 py-1 pl-6 text-gray-600 dark:text-gray-400 border-r border-blue-50 dark:border-blue-900/20">code</div>
                          <div className="w-1/2 px-3 py-1 text-orange-600 dark:text-orange-400">0</div>
                        </div>
                        {/* msg */}
                        <div className="flex border-b border-blue-50 dark:border-blue-900/20">
                          <div className="w-1/2 px-3 py-1 pl-6 text-gray-600 dark:text-gray-400 border-r border-blue-50 dark:border-blue-900/20">msg</div>
                          <div className="w-1/2 px-3 py-1 text-green-600 dark:text-green-400">"success"</div>
                        </div>
                        {/* data */}
                        <div className="flex border-b border-blue-50 dark:border-blue-900/20">
                          <div className="w-1/2 px-3 py-1 pl-6 text-gray-600 dark:text-gray-400 border-r border-blue-50 dark:border-blue-900/20">
                            <span className="text-gray-400 mr-1">▼</span> data
                          </div>
                          <div className="w-1/2 px-3 py-1 text-purple-600 dark:text-purple-400">{'{...}'}</div>
                        </div>
                        {/* data.total */}
                        <div className="flex border-b border-blue-50 dark:border-blue-900/20">
                          <div className="w-1/2 px-3 py-1 pl-10 text-gray-600 dark:text-gray-400 border-r border-blue-50 dark:border-blue-900/20">total</div>
                          <div className="w-1/2 px-3 py-1 text-orange-600 dark:text-orange-400">3</div>
                        </div>
                        {/* data.list 展开 */}
                        <div className="flex border-b border-blue-50 dark:border-blue-900/20">
                          <div className="w-1/2 px-3 py-1 pl-10 text-gray-600 dark:text-gray-400 border-r border-blue-50 dark:border-blue-900/20">
                            <span className="text-gray-400 mr-1">▼</span> list
                          </div>
                          <div className="w-1/2 px-3 py-1 text-purple-600 dark:text-purple-400">[...]</div>
                        </div>
                        {/* list 数组表格 */}
                        <div className="ml-10 mr-2 my-1 border border-blue-100 dark:border-blue-900/30 rounded overflow-hidden">
                          {/* 表格头 */}
                          <div className="flex text-xs font-medium bg-gray-100 dark:bg-gray-700/50 border-b border-blue-100 dark:border-blue-900/30">
                            <div className="w-8 px-2 py-1 text-gray-500 dark:text-gray-400 border-r border-blue-100 dark:border-blue-900/30">#</div>
                            <div className="flex-1 px-2 py-1 text-gray-600 dark:text-gray-300 border-r border-blue-100 dark:border-blue-900/30">id</div>
                            <div className="flex-1 px-2 py-1 text-gray-600 dark:text-gray-300 border-r border-blue-100 dark:border-blue-900/30">name</div>
                            <div className="flex-1 px-2 py-1 text-gray-600 dark:text-gray-300">status</div>
                          </div>
                          {/* 表格数据行 */}
                          <div className="flex text-xs border-b border-blue-50 dark:border-blue-900/20">
                            <div className="w-8 px-2 py-1 text-gray-400 border-r border-blue-50 dark:border-blue-900/20">0</div>
                            <div className="flex-1 px-2 py-1 text-green-600 dark:text-green-400 border-r border-blue-50 dark:border-blue-900/20">"u001"</div>
                            <div className="flex-1 px-2 py-1 text-green-600 dark:text-green-400 border-r border-blue-50 dark:border-blue-900/20">"Alice"</div>
                            <div className="flex-1 px-2 py-1 text-orange-600 dark:text-orange-400">1</div>
                          </div>
                          <div className="flex text-xs border-b border-blue-50 dark:border-blue-900/20">
                            <div className="w-8 px-2 py-1 text-gray-400 border-r border-blue-50 dark:border-blue-900/20">1</div>
                            <div className="flex-1 px-2 py-1 text-green-600 dark:text-green-400 border-r border-blue-50 dark:border-blue-900/20">"u002"</div>
                            <div className="flex-1 px-2 py-1 text-green-600 dark:text-green-400 border-r border-blue-50 dark:border-blue-900/20">"Bob"</div>
                            <div className="flex-1 px-2 py-1 text-orange-600 dark:text-orange-400">0</div>
                          </div>
                          <div className="flex text-xs">
                            <div className="w-8 px-2 py-1 text-gray-400 border-r border-blue-50 dark:border-blue-900/20">2</div>
                            <div className="flex-1 px-2 py-1 text-green-600 dark:text-green-400 border-r border-blue-50 dark:border-blue-900/20">"u003"</div>
                            <div className="flex-1 px-2 py-1 text-green-600 dark:text-green-400 border-r border-blue-50 dark:border-blue-900/20">"Carol"</div>
                            <div className="flex-1 px-2 py-1 text-orange-600 dark:text-orange-400">1</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 可拖动分隔线 */}
        {!isInputCollapsed && (
          <ResizableSplitter
            leftWidth={leftWidth}
            onResize={handleResize}
            onDragStateChange={handleDragStateChange}
            minWidth={20}
            maxWidth={70}
            collapseThreshold={5}
          />
        )}

        {/* 右侧：视图区域（树状/表格） */}
        <div
          className={`flex flex-col ${isDragging ? '' : 'transition-all duration-500 ease-out'}`}
          style={{ width: isInputCollapsed ? '100%' : `${100 - leftWidth}%` }}
        >
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {viewMode === 'tree' ? t('json.view.treeView') : t('json.view.tableView')}
            </h3>
          </div>

          {/* 根据 viewMode 渲染不同视图 */}
          {viewMode === 'tree' ? (
            <JsonTreeView
              tree={treeData}
              onToggle={handleToggleNode}
              selectedNode={selectedNode}
              onNodeSelect={detailMode ? handleNodeSelect : null}
              isInputCollapsed={isInputCollapsed}
              detailMode={detailMode}
            />
          ) : (
            <PreviewTable tree={treeData} onToggle={handleToggleNode} />
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default JsonFormatView;
